import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field, FieldLabel } from "@/components/ui/field";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PermissionGate } from "@/shared/components/PermissionGate";
import { usePermission } from "@/shared/hooks/usePermissions";
import { useConfirm } from "@/shared/contexts/confirm-context";
import { useNotification } from "@/shared/hooks/useNotification";
import { Smartphone } from "lucide-react";
import { LoadingAnimation } from "@/shared/components/LoadingAnimation";
import {
  useMobileAccessStatus,
  useEnableMobileAccess,
  useResendMobileInvite,
  useRevokeMobileAccess,
  useCustomerMembers,
  useInviteCustomerMember,
  useChangeCustomerMemberRole,
  useCustomerMemberAction,
} from "../hooks/useMobileAccess";
import type { CustomerAccessRole, MobileAccessStatusValue } from "../services/mobileAccessApi";

const STATUS_META: Record<
  MobileAccessStatusValue,
  { label: string; variant: "default" | "secondary" | "destructive" | "outline" }
> = {
  None: { label: "Not Enabled", variant: "outline" },
  PendingInvitation: { label: "Pending Invitation", variant: "secondary" },
  Active: { label: "Active", variant: "default" },
  Suspended: { label: "Suspended", variant: "secondary" },
  Revoked: { label: "Revoked", variant: "destructive" },
};

export function MobileAccessSection({ customerId, customerType }: { customerId: string; customerType: "Individual" | "Organization" }) {
  if (customerType === "Organization") return <OrganizationMembersSection customerId={customerId} />;
  return <IndividualMobileAccessSection customerId={customerId} />;
}

function IndividualMobileAccessSection({ customerId }: { customerId: string }) {
  const canView = usePermission(["customers.mobile_access.view"], "any");
  const { data, isLoading, isError } = useMobileAccessStatus(customerId, canView);
  const enable = useEnableMobileAccess(customerId);
  const resend = useResendMobileInvite(customerId);
  const revoke = useRevokeMobileAccess(customerId);
  const confirm = useConfirm();
  const notification = useNotification();

  const status = data?.status ?? "None";
  const meta = STATUS_META[status];
  const busy = enable.isPending || resend.isPending || revoke.isPending;

  // Hidden entirely for users who can't administer mobile access (Admin-only for now).
  if (!canView) return null;

  const runEnable = () =>
    confirm({
      title: status === "Revoked" ? "Re-enable mobile access?" : "Enable mobile access?",
      description:
        "This creates a mobile login for the customer and sends a password-setup invite by SMS/email.",
      confirmLabel: status === "Revoked" ? "Enable again" : "Enable Mobile Access",
      onConfirm: () =>
        enable
          .mutateAsync()
          .then(() => notification.success("Mobile access enabled — invite sent"))
          .catch(() => notification.error("Failed to enable mobile access")),
    });

  const runResend = () =>
    resend
      .mutateAsync()
      .then(() => notification.success("Invite resent"))
      .catch(() => notification.error("Failed to resend invite"));

  const runRevoke = () =>
    confirm({
      title: "Disable mobile access?",
      description: "The customer will be signed out and blocked from the mobile app.",
      confirmLabel: "Disable",
      onConfirm: () =>
        revoke
          .mutateAsync(undefined)
          .then(() => notification.success("Mobile access revoked"))
          .catch(() => notification.error("Failed to revoke mobile access")),
    });

  return (
    <>
      {(enable.isPending || resend.isPending) && (
        <LoadingAnimation
          fullScreen
          className="z-[100]"
          text={resend.isPending ? "Resending mobile invitation…" : "Creating mobile account and sending invitation…"}
        />
      )}
      <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="flex items-center gap-2 text-base">
          <Smartphone className="h-4 w-4" /> Mobile Access
        </CardTitle>
        {!isLoading && !isError && <Badge variant={meta.variant}>{meta.label}</Badge>}
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
        {isError && <p className="text-sm text-destructive">Could not load mobile access status.</p>}

        {!isLoading && !isError && (
          <>
            {data && data.users.length > 0 && (
              <div className="space-y-1 text-sm">
                {data.users.map((u) => (
                  <div key={u.linkId} className="flex items-center justify-between gap-2">
                    <span className="text-muted-foreground">
                      {u.fullName || u.username}
                      {u.phoneNumber ? ` · ${u.phoneNumber}` : ""}
                    </span>
                    <Badge variant={STATUS_META[u.status].variant} className="text-xs">
                      {STATUS_META[u.status].label}
                    </Badge>
                  </div>
                ))}
              </div>
            )}

            <div className="flex flex-wrap gap-2">
              {(status === "None" || status === "Revoked") && (
                <PermissionGate permissions={["customers.mobile_access.enable"]}>
                  <Button size="sm" disabled={busy} onClick={runEnable}>
                    {status === "Revoked" ? "Enable Again" : "Enable Mobile Access"}
                  </Button>
                </PermissionGate>
              )}

              {status === "PendingInvitation" && (
                <>
                  <PermissionGate permissions={["customers.mobile_access.enable"]}>
                    <Button size="sm" variant="outline" disabled={busy} onClick={runResend}>
                      Resend Invite
                    </Button>
                  </PermissionGate>
                  <PermissionGate permissions={["customers.mobile_access.revoke"]}>
                    <Button size="sm" variant="destructive" disabled={busy} onClick={runRevoke}>
                      Revoke
                    </Button>
                  </PermissionGate>
                </>
              )}

              {status === "Active" && (
                <PermissionGate permissions={["customers.mobile_access.revoke"]}>
                  <Button size="sm" variant="destructive" disabled={busy} onClick={runRevoke}>
                    Disable Mobile Access
                  </Button>
                </PermissionGate>
              )}
            </div>
          </>
        )}
      </CardContent>
      </Card>
    </>
  );
}

const ACCESS_ROLES: Array<{ value: CustomerAccessRole; label: string }> = [
  { value: "AccountAdmin", label: "Account administrator" },
  { value: "Operations", label: "Operations" },
  { value: "Finance", label: "Finance" },
  { value: "ReadOnly", label: "Read only" },
];

function OrganizationMembersSection({ customerId }: { customerId: string }) {
  const canView = usePermission(["customers.mobile_access.view"], "any");
  const canInvite = usePermission(["customers.mobile_access.enable"], "any");
  const canRevoke = usePermission(["customers.mobile_access.revoke"], "any");
  const [pageNumber, setPageNumber] = useState(1);
  const pageSize = 25;
  const members = useCustomerMembers(customerId, pageNumber, pageSize, canView);
  const invite = useInviteCustomerMember(customerId);
  const changeRole = useChangeCustomerMemberRole(customerId);
  const memberAction = useCustomerMemberAction(customerId);
  const notify = useNotification();
  const confirm = useConfirm();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [draft, setDraft] = useState({
    firstName: "", otherNames: "", phoneNumber: "", email: "", jobTitle: "", accessRole: "ReadOnly" as CustomerAccessRole,
  });

  if (!canView) return null;
  const busy = invite.isPending || changeRole.isPending || memberAction.isPending;
  const smsMemberActionPending =
    memberAction.isPending &&
    (memberAction.variables?.action === "resend-invitation" || memberAction.variables?.action === "reinvite");

  const submitInvite = async () => {
    try {
      await invite.mutateAsync(draft);
      notify.success("Personnel invitation sent");
      setInviteOpen(false);
      setDraft({ firstName: "", otherNames: "", phoneNumber: "", email: "", jobTitle: "", accessRole: "ReadOnly" });
    } catch {
      notify.error("Could not invite this person");
    }
  };

  const runAction = (membershipId: string, action: "resend-invitation" | "suspend" | "reactivate" | "revoke" | "reinvite") => {
    const destructive = action === "suspend" || action === "revoke";
    const execute = () => memberAction.mutateAsync({ membershipId, action })
      .then(() => notify.success("Member access updated"))
      .catch(() => notify.error("Could not update member access"));
    if (!destructive) return void execute();
    confirm({
      title: action === "revoke" ? "Revoke this member?" : "Suspend this member?",
      description: "The change takes effect immediately for this customer account.",
      confirmLabel: action === "revoke" ? "Revoke" : "Suspend",
      onConfirm: execute,
    });
  };

  return (
    <>
      {(invite.isPending || smsMemberActionPending) && (
        <LoadingAnimation
          fullScreen
          className="z-[100]"
          text={invite.isPending ? "Creating account and sending invitation…" : "Resending mobile invitation…"}
        />
      )}
      <Card className="md:col-span-2 xl:col-span-3">
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3 space-y-0">
        <CardTitle className="flex items-center gap-2 text-base"><Smartphone className="h-4 w-4" /> Organization Personnel</CardTitle>
        {canInvite && <Button size="sm" onClick={() => setInviteOpen(true)}>Invite Personnel</Button>}
      </CardHeader>
      <CardContent>
        {members.isLoading && <p className="text-sm text-muted-foreground">Loading personnel…</p>}
        {members.isError && <p className="text-sm text-destructive">Could not load organization personnel.</p>}
        {members.data && (<>
          <div className="min-h-64 max-h-[28rem] overflow-auto rounded-md border">
            <Table className="min-w-[760px]">
              <TableHeader className="sticky top-0 z-10 bg-card"><TableRow><TableHead>Person</TableHead><TableHead>Role</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
              <TableBody>
                {members.data.items.map((member) => (
                  <TableRow key={member.membershipId}>
                    <TableCell><div className="font-medium">{member.fullName}</div><div className="text-xs text-muted-foreground">{member.phoneNumber}{member.jobTitle ? ` · ${member.jobTitle}` : ""}</div></TableCell>
                    <TableCell>
                      <Select value={member.accessRole} disabled={!canInvite || busy} onValueChange={(accessRole: CustomerAccessRole) => changeRole.mutate({ membershipId: member.membershipId, accessRole })}>
                        <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
                        <SelectContent>{ACCESS_ROLES.map((role) => <SelectItem key={role.value} value={role.value}>{role.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell><Badge variant={STATUS_META[member.status].variant}>{STATUS_META[member.status].label}</Badge></TableCell>
                    <TableCell className="space-x-2 text-right">
                      {member.status === "PendingInvitation" && canInvite && <Button size="sm" variant="outline" disabled={busy} onClick={() => runAction(member.membershipId, "resend-invitation")}>Resend</Button>}
                      {member.status === "Active" && canRevoke && <Button size="sm" variant="outline" disabled={busy} onClick={() => runAction(member.membershipId, "suspend")}>Suspend</Button>}
                      {member.status === "Suspended" && canInvite && <Button size="sm" variant="outline" disabled={busy} onClick={() => runAction(member.membershipId, "reactivate")}>Reactivate</Button>}
                      {member.status === "Revoked" && canInvite && <Button size="sm" variant="outline" disabled={busy} onClick={() => runAction(member.membershipId, "reinvite")}>Reinvite</Button>}
                      {(member.status === "Active" || member.status === "Suspended" || member.status === "PendingInvitation") && canRevoke && <Button size="sm" variant="destructive" disabled={busy} onClick={() => runAction(member.membershipId, "revoke")}>Revoke</Button>}
                    </TableCell>
                  </TableRow>
                ))}
                {members.data.items.length === 0 && <TableRow><TableCell colSpan={4} className="h-52 text-center text-muted-foreground">No personnel have been invited.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
          {members.data.totalCount > pageSize && (
            <div className="mt-4 flex items-center justify-between text-sm">
              <span className="text-muted-foreground">
                Page {members.data.pageNumber} of {Math.ceil(members.data.totalCount / members.data.pageSize)} · {members.data.totalCount} personnel
              </span>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" disabled={pageNumber === 1 || members.isFetching} onClick={() => setPageNumber((page) => Math.max(1, page - 1))}>Previous</Button>
                <Button size="sm" variant="outline" disabled={pageNumber * pageSize >= members.data.totalCount || members.isFetching} onClick={() => setPageNumber((page) => page + 1)}>Next</Button>
              </div>
            </div>
          )}
        </>)}
      </CardContent>

      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent className="max-h-[calc(100vh-2rem)] w-[calc(100vw-2rem)] overflow-y-auto sm:max-w-2xl">
          <DialogHeader className="border-b pb-4">
            <DialogTitle>Invite organization personnel</DialogTitle>
            <DialogDescription>Add a person and choose the access they will have for this customer account.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-x-5 gap-y-4 py-2 sm:grid-cols-2">
            <Field><FieldLabel>First name</FieldLabel><Input value={draft.firstName} onChange={(event) => setDraft({ ...draft, firstName: event.target.value })} /></Field>
            <Field><FieldLabel>Other names</FieldLabel><Input value={draft.otherNames} onChange={(event) => setDraft({ ...draft, otherNames: event.target.value })} /></Field>
            <Field><FieldLabel>Phone number</FieldLabel><Input value={draft.phoneNumber} onChange={(event) => setDraft({ ...draft, phoneNumber: event.target.value })} /></Field>
            <Field><FieldLabel>Email</FieldLabel><Input type="email" value={draft.email} onChange={(event) => setDraft({ ...draft, email: event.target.value })} /></Field>
            <Field><FieldLabel>Job title</FieldLabel><Input value={draft.jobTitle} onChange={(event) => setDraft({ ...draft, jobTitle: event.target.value })} /></Field>
            <Field><FieldLabel>Access role</FieldLabel><Select value={draft.accessRole} onValueChange={(accessRole: CustomerAccessRole) => setDraft({ ...draft, accessRole })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{ACCESS_ROLES.map((role) => <SelectItem key={role.value} value={role.value}>{role.label}</SelectItem>)}</SelectContent></Select></Field>
          </div>
          <DialogFooter className="border-t pt-4"><Button variant="outline" onClick={() => setInviteOpen(false)}>Cancel</Button><Button disabled={busy || !draft.firstName.trim() || !draft.phoneNumber.trim()} onClick={() => void submitInvite()}>Send Invitation</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      </Card>
    </>
  );
}
